import { useEffect, useRef, type RefObject } from 'react';

/**
 * For a notice, banner or inline editor that takes itself off the page while focus is on its own button
 * (WCAG 2.4.3): the button unmounts and focus would fall to <body>. The element that had focus when the notice
 * appeared gets it back.
 *
 *   const ref = useRef<HTMLDivElement>(null);
 *   useReturnFocusOnUnmount(asked, ref);   // `active` false -> true is "appeared", true -> false (or unmount) is "gone"
 *
 * The opener is read while rendering the first active state. On the way out focus is restored only when it would
 * otherwise be lost (nothing, <body> or a removed node holds it) and the opener is still in the page. With a
 * `containerRef`, only when focus was last inside the container, so a notice that times out on its own never pulls
 * focus away from where the user is. `returnToRef` names the element to restore when the one captured on appearing is
 * not the right one (a menu item that is about to unmount opened the editor: the button that opened the menu is);
 * it is read when the notice goes, and used only while it is still on the page.
 */
export function useReturnFocusOnUnmount(
  active = true,
  containerRef?: RefObject<HTMLElement | null>,
  returnToRef?: RefObject<HTMLElement | null>,
) {
  const openerRef = useRef<HTMLElement | null>(null);
  const wasActiveRef = useRef(false);
  const insideRef = useRef(false);
  if (active !== wasActiveRef.current) {
    wasActiveRef.current = active;
    if (active && typeof document !== 'undefined') openerRef.current = document.activeElement as HTMLElement | null;
  }

  useEffect(() => {
    if (!active) return;
    insideRef.current = false;
    const container = () => containerRef?.current ?? null;
    const onFocusIn = (event: FocusEvent) => {
      const el = container();
      insideRef.current = !!el && event.target instanceof Node && el.contains(event.target);
    };
    const onFocusOut = (event: FocusEvent) => {
      const el = container();
      if (!el || !event.target || !(event.target instanceof Node) || !el.contains(event.target)) return;
      const next = event.relatedTarget;
      // Focus moving to another element outside, or to <body> while the container is still on the page, is a real leave;
      // a blur caused by the container being removed is not.
      if ((next instanceof Node && !el.contains(next)) || (!next && el.isConnected)) insideRef.current = false;
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    const seed = container();
    insideRef.current = !!seed && !!document.activeElement && seed.contains(document.activeElement);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (containerRef && !insideRef.current) return;
      const preferred = returnToRef?.current;
      const opener = preferred && preferred.isConnected ? preferred : openerRef.current;
      const current = document.activeElement;
      const lost = !current || current === document.body || !current.isConnected;
      if (lost && opener && opener !== document.body && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [active, containerRef, returnToRef]);
}
